'use client';

import React, { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowBack, ArrowForward, Check } from '@mui/icons-material';
import { Box, MobileStepper, Step, Stepper } from '@mui/material';

import { useBreakpoint, useIsSessionExpired, useTheme } from '@dh/hooks';
import { Column, LabelLarge, LabelMedium } from '@dh/styles';
import { Breakpoint, isMedium } from '@dh/types';
import { userSignOut } from '@dh/auth/authSession';

import { FilledButton, IconButton, OutlinedButton, TextButton } from '../../buttons';

import {
    CustomisedUnorderedConnector,
    StepperContainer,
    StepperContent,
    StepperDot,
    StepperLabel,
    StyledForm,
    UnorderedStepperButtonRow
} from '../styles';
import { StepDetails } from '../types';

type Props = {
    activeStep: number;
    steps: StepDetails[];
    onChangeStep: (stepIndex: number) => void;
    onSave?: () => void;
    isSaveDisabled?: boolean;
    showStepIcons?: boolean;
    lastStepComponent?: React.ReactNode;
    showErrorSteps?: boolean;
    showCompletedSteps?: boolean;
};

const UnorderedStepper = ({
    activeStep,
    steps,
    onChangeStep,
    onSave,
    isSaveDisabled,
    showStepIcons = true,
    lastStepComponent = null,
    showErrorSteps = true,
    showCompletedSteps = true
}: Props) => {
    const t = useTranslations('dialog');
    const breakpoint = useBreakpoint();
    const { colors } = useTheme();

    const contentContainerRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const isLastStep = activeStep === steps.length - 1;
    const isFirstStep = activeStep === 0;
    const isExpanded = breakpoint === Breakpoint.Expanded;
    const isSessionExpired = useIsSessionExpired();

    const handleSave = () => {
        if (isSessionExpired) userSignOut();
        else onSave?.();
    };

    const handleNext = () => {
        if (isSessionExpired) userSignOut();
        else onChangeStep(activeStep + 1);
    };

    const handleBack = () => {
        if (isSessionExpired) userSignOut();
        else onChangeStep(activeStep - 1);
    };

    const handleChangeStep = (newStep: number) => {
        if (isSessionExpired) userSignOut();
        else onChangeStep(newStep);
    };

    const renderDot = (isActive: boolean) => (
        <StepperDot isActive={isActive}>
            <Check
                sx={{
                    height: 20,
                    width: 20,
                    color: isActive ? colors.primaryContainerDark : colors.white
                }}
            />
        </StepperDot>
    );

    return (
        <StepperContainer isExpanded={isExpanded}>
            {!isExpanded ? (
                <MobileStepper
                    variant='dots'
                    steps={steps.length}
                    position='static'
                    activeStep={activeStep}
                    nextButton={
                        <IconButton
                            icon={<ArrowForward />}
                            onClick={handleNext}
                            disabled={isLastStep}
                        />
                    }
                    backButton={
                        <IconButton
                            icon={<ArrowBack />}
                            onClick={handleBack}
                            disabled={isFirstStep}
                        />
                    }
                />
            ) : (
                <Stepper
                    activeStep={activeStep}
                    nonLinear={true}
                    orientation={!isExpanded ? 'horizontal' : 'vertical'}
                    role={'navigation'}
                    connector={<CustomisedUnorderedConnector breakpoint={breakpoint} />}
                    // We need to inline style here to be able to pass the custom connector to the stepper. This does not work with a styled component stepper.
                    sx={{
                        height: 'fit-content',
                        width: isExpanded ? '301px' : 'auto',
                        mb: 3
                    }}
                >
                    {steps.map(({ label, description, isCompleted, hasErrors }, index) => {
                        return (
                            <Step
                                key={label}
                                onClick={() => handleChangeStep(index)}
                                completed={showCompletedSteps ? isCompleted : false}
                            >
                                <StepperLabel
                                    sx={{ p: 1 }}
                                    error={showErrorSteps ? hasErrors : false}
                                    slots={{
                                        // TODO Add finish icon, when step is finished
                                        stepIcon: showStepIcons
                                            ? () => renderDot(activeStep === index)
                                            : undefined
                                    }}
                                >
                                    {isExpanded && (
                                        <Column>
                                            <LabelLarge>{label}</LabelLarge>
                                            <LabelMedium>{description}</LabelMedium>
                                        </Column>
                                    )}
                                </StepperLabel>
                            </Step>
                        );
                    })}
                </Stepper>
            )}
            <StepperContent isMedium={isMedium(breakpoint)}>
                <StyledForm ref={formRef} isExpanded={isExpanded}>
                    <Column gap={4} ref={contentContainerRef}>
                        {steps[activeStep].content}
                    </Column>
                </StyledForm>
                <UnorderedStepperButtonRow
                    hasBorder={true}
                    isLastStep={isLastStep}
                    breakpoint={breakpoint}
                >
                    {!isFirstStep ? (
                        <OutlinedButton
                            aria-label={`${t('back')} Button`}
                            onClick={handleBack}
                            label={t('back')}
                            sx={{ justifySelf: 'flex-start' }}
                        />
                    ) : (
                        <Box />
                    )}
                    <TextButton
                        aria-label={`${t('save')} Button`}
                        sx={{
                            /* we hide the save button if no save function is provided, but we still need the space */
                            opacity: onSave === undefined ? 0 : 1
                        }}
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        label={t('save')}
                    />
                    {!isLastStep && (
                        <FilledButton
                            aria-label={`${t('continue')} Button`}
                            onClick={handleNext}
                            label={t('continue')}
                            sx={{ justifySelf: 'flex-end' }}
                        />
                    )}
                    {isLastStep && lastStepComponent ? lastStepComponent : null}
                </UnorderedStepperButtonRow>
            </StepperContent>
        </StepperContainer>
    );
};

export { UnorderedStepper };
